/**
 * JAISIU-3503 / processing-transparency — non-blocking network-blip toast.
 *
 * Renders a thin, ARIA-live-polite banner above the chat thread when
 * the gateway connection flaps. The toast carries:
 *   - a headline + one-line detail
 *   - a Retry-now button (manual override)
 *   - a Dismiss button (silences the toast without affecting the run)
 *   - a live countdown to the next auto-retry
 *
 * The toast never blocks the run: the activity pill keeps ticking,
 * the live stream keeps rendering, and the user can keep typing in
 * the composer. When the host's gateway event lands (reconnect OK
 * or final), the host dispatches a `resolve` action and the toast
 * fades out.
 *
 * The bar uses `position: sticky` (via the parent's class) so it
 * stays anchored at the top of the chat thread on scroll.
 */
import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import type { NetworkBlipState } from "./chat-network-blip.ts";
import { formatCountdownSeconds, isBlipVisible } from "./chat-network-blip.ts";

export function renderNetworkBlipToast(params: {
  blip: NetworkBlipState | null;
  now: number;
  onRetryNow: () => void;
  onDismiss: () => void;
  /** Optional test id override. */
  testId?: string;
}) {
  const { blip, now, onRetryNow, onDismiss, testId = "chat-network-blip" } = params;
  if (!isBlipVisible(blip)) {
    return nothing;
  }
  const seconds = formatCountdownSeconds(blip, now);
  const countdownLabel = blip.autoRetryArmed
    ? `Auto-retry in ${seconds}s`
    : "Auto-retry pending";
  return html`
    <div
      class="chat-network-blip"
      role="status"
      aria-live="polite"
      data-testid=${testId}
      data-blip-kind=${blip.kind}
    >
      <span class="chat-network-blip__icon" aria-hidden="true">${icons.info}</span>
      <div class="chat-network-blip__body">
        <div class="chat-network-blip__title">${blip.title}</div>
        <div class="chat-network-blip__detail">${blip.detail}</div>
        <div class="chat-network-blip__meta">
          <span>Attempt ${blip.attempt + 1}</span>
          <span aria-hidden="true">·</span>
          <span>${countdownLabel}</span>
        </div>
      </div>
      <div class="chat-network-blip__actions">
        <button
          type="button"
          class="chat-network-blip__retry"
          data-testid="${testId}-retry"
          @click=${onRetryNow}
        >
          ${icons.refreshCw}
          <span>Retry now</span>
        </button>
        <button
          type="button"
          class="chat-network-blip__dismiss"
          data-testid="${testId}-dismiss"
          @click=${onDismiss}
          aria-label="Dismiss the network blip notification"
          title="Dismiss"
        >
          ${icons.x}
        </button>
      </div>
    </div>
  `;
}
